// "Create a task from this event": what a new task takes from a Google event.
// Only what the person sees on the card is copied, into an ordinary task; the
// task keeps no event id and no link back, so nothing about the event itself
// is stored.

import type { DateStr, Recurrence, TaskKind, TimeStr } from '@/types'
import { isoWeekday, parts } from './dates'
import type { CalEvent } from './events'

const FREQS: Record<string, Recurrence['freq']> = { DAILY: 'day', WEEKLY: 'week', MONTHLY: 'month', YEARLY: 'year' }
const WEEKDAYS: Record<string, number> = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 7 }
// an end date or a count is dropped (a repeat runs on until it is changed); anything else narrows the
// pattern in a way Cuplzap's rules cannot hold
const HARMLESS = new Set(['FREQ', 'INTERVAL', 'BYDAY', 'BYMONTHDAY', 'BYMONTH', 'WKST', 'UNTIL', 'COUNT'])

/**
 * A Google series' RRULE as a Cuplzap repeat starting on `start`, or null when the pattern has no
 * equal here, such as "the second Tuesday of each month". Days, weeks, months and years, any
 * interval; weekdays on a weekly rule; one day of the month on a monthly rule, the last day included.
 */
export function recurrenceFromRRule(lines: string[] | null | undefined, start: DateStr, startTime: TimeStr | null): Recurrence | null {
  const line = lines?.find((l) => l.toUpperCase().startsWith('RRULE:'))
  if (!line) return null
  const rule = new Map<string, string>()
  for (const part of line.slice(6).split(';')) {
    const [k, v] = part.split('=')
    if (k && v) rule.set(k.toUpperCase(), v.toUpperCase())
  }
  if ([...rule.keys()].some((k) => !HARMLESS.has(k))) return null
  let freq = FREQS[rule.get('FREQ') ?? '']
  if (!freq) return null
  const interval = Number(rule.get('INTERVAL') ?? '1')
  if (!Number.isInteger(interval) || interval < 1) return null

  const byDay = rule.get('BYDAY')?.split(',') ?? null
  // a weekday with a number in front ("2TU", "-1FR") counts weeks within a month or year
  if (byDay && byDay.some((d) => !(d in WEEKDAYS))) return null
  const byMonthDay = rule.get('BYMONTHDAY')?.split(',') ?? null
  const byMonth = rule.get('BYMONTH')?.split(',') ?? null

  let weekdays: number[] | null = null
  let monthDay: number | null = null
  // "every day, but only Mon to Fri" is a weekly rule on those days
  if (freq === 'day' && byDay) {
    if (interval !== 1) return null
    freq = 'week'
  }
  if (freq === 'week') {
    if (byMonthDay || byMonth) return null
    weekdays = byDay ? [...new Set(byDay.map((d) => WEEKDAYS[d]))].sort((a, b) => a - b) : [isoWeekday(start)]
  } else if (freq === 'month') {
    if (byDay || byMonth) return null
    if (byMonthDay) {
      if (byMonthDay.length !== 1) return null
      const n = Number(byMonthDay[0])
      // the last day of the month: a day of 31 is clamped to each month's last
      if (n === -1) monthDay = 31
      else if (Number.isInteger(n) && n >= 1 && n <= 31) monthDay = n
      else return null
    } else monthDay = parts(start).d
  } else if (freq === 'year') {
    // a yearly rule repeats on the start's own day and month, which is all Cuplzap's yearly rule does
    if (byDay) return null
    if (byMonth && (byMonth.length !== 1 || Number(byMonth[0]) !== parts(start).m)) return null
    if (byMonthDay && (byMonthDay.length !== 1 || Number(byMonthDay[0]) !== parts(start).d)) return null
  } else if (byDay || byMonthDay || byMonth) return null

  return { freq, interval, weekdays, monthDay, basis: 'schedule', scheduledDate: start, scheduledTime: startTime }
}

export interface EventTaskFields {
  title: string
  description: string | null
  kind: TaskKind
  plannedDate: DateStr | null
  dueDate: DateStr
  dueTime: TimeStr | null
  recurrence: Recurrence | null
}

/**
 * The new task's fields: the event's title, its place, video link and description, and its day and
 * start time as the due date and time. One event is a one-off planned for its day; one of a series
 * whose pattern Cuplzap can hold is a repeat due on this occurrence, which its pattern then rolls on.
 * `description` is the card's text, the HTML already taken out.
 */
export function taskFromEvent(
  ev: Pick<CalEvent, 'title' | 'allDay' | 'startDate' | 'startTime' | 'location' | 'videoLink'>,
  description: string | null,
  rrule: string[] | null,
): EventTaskFields {
  const dueTime = ev.allDay ? null : ev.startTime
  const recurrence = recurrenceFromRRule(rrule, ev.startDate, dueTime)
  const notes = [description?.trim() || null, ev.location ? `Where: ${ev.location}` : null, ev.videoLink ? `Video call: ${ev.videoLink}` : null].filter((s): s is string => !!s)
  return {
    title: ev.title.trim().slice(0, 200),
    description: notes.length ? notes.join('\n\n').slice(0, 10000) : null,
    kind: recurrence ? 'repeat' : 'once',
    // a repeat sits on the day its pattern brings it round on; a one-off needs a Do it on day to sit on one
    plannedDate: recurrence ? null : ev.startDate,
    dueDate: ev.startDate,
    dueTime,
    recurrence,
  }
}
